/**
 * Who we no longer mail, and why.
 *
 * Delivery tracking has been writing `skidaCrmEmailSuppressions` since the
 * webhook went live, and `resolveAudience` subtracts it from every audience —
 * so audiences already shrink for hard bounces and spam complaints. Nothing
 * showed the list. The preview names a count, and "who?" is the first question
 * anybody asks when a number drops.
 *
 * Read-only, and not because it was quicker. Removing an entry means mailing
 * somebody whose mailbox does not exist, or who pressed the spam button — the
 * Firestore rules refuse a client write, and this screen has no button the rules
 * would have to refuse.
 *
 * Three reasons live here, and they are not the same fact:
 *
 *   - **bounced** — the address is dead. A permanent failure only; a full
 *     mailbox or a greylist is transient and never lands here, or the list
 *     would shrink every time somebody went on holiday.
 *   - **complained** — the person marked a message as spam. That is a decision,
 *     not a failure, and it is the one nobody may undo from a screen.
 *   - **unsubscribed** — they used the footer link, and have no skida.app
 *     account for the choice to live on instead. Every cold lead is in that
 *     case. It reads as a bad outcome next to the other two and is not one: it
 *     is the opt-out working, and it is what makes a cold send lawful at all.
 *
 * Only a bounce withholds service mail. The other two stop marketing and leave
 * a receipt or a password reset alone — see `audience.ts`.
 */
import { useEffect, useMemo, useState } from 'react'
import { formatDate } from '@/shared/util/dates'
import { haystack, matchesTokens } from '@/shared/util/tokenSearch'
import {
  subscribeToSuppressions,
  type Suppression,
  type SuppressionReason,
} from './repository/suppressionsRepository'
import './Suppressions.css'

const REASONS: { key: SuppressionReason; label: string; color: string; bg: string }[] = [
  { key: 'bounced', label: 'Bounced', color: '#7b5200', bg: '#fff3d8' },
  { key: 'complained', label: 'Marked as spam', color: '#c23b3b', bg: '#fdeaea' },
  // Deliberately not a warning colour. See the header: this one is the system
  // working, and a red badge would have a rep reading it as a failure.
  { key: 'unsubscribed', label: 'Unsubscribed', color: '#4a5560', bg: '#eef1f4' },
]

const style = (r: SuppressionReason) => REASONS.find((x) => x.key === r) ?? REASONS[0]!

export function Suppressions() {
  const [rows, setRows] = useState<Suppression[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [term, setTerm] = useState('')
  const [reason, setReason] = useState<SuppressionReason | 'all'>('all')

  useEffect(() => {
    const un = subscribeToSuppressions(
      (r) => {
        setRows(r)
        setLoading(false)
      },
      (e) => {
        setError(e.message)
        setLoading(false)
      },
    )
    return un
  }, [])

  const counts = useMemo(() => {
    const m = new Map<SuppressionReason, number>()
    for (const r of rows) m.set(r.reason, (m.get(r.reason) ?? 0) + 1)
    return m
  }, [rows])

  const shown = useMemo(
    () =>
      rows
        .filter((r) => reason === 'all' || r.reason === reason)
        .filter((r) => matchesTokens(term, (token) => haystack([r.email, r.detail]).includes(token))),
    [rows, reason, term],
  )

  return (
    <section className="supp">
      <div className="supp__head">
        <div>
          <h3 className="supp__title">Not mailed</h3>
          <p className="supp__sub">
            {loading
              ? 'Loading…'
              : `${rows.length} address${rows.length === 1 ? '' : 'es'} held back from every audience`}
          </p>
        </div>
      </div>

      {error && <div className="empty-note">Could not read the list: {error}</div>}

      <div className="supp__filters">
        <input
          className="input supp__search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Address or reason…"
          aria-label="Search suppressed addresses"
        />
        <button className="chip" aria-pressed={reason === 'all'} onClick={() => setReason('all')}>
          All
        </button>
        {REASONS.map((r) => (
          <button
            key={r.key}
            className="chip"
            aria-pressed={reason === r.key}
            onClick={() => setReason(r.key)}
          >
            {r.label} ({counts.get(r.key) ?? 0})
          </button>
        ))}
      </div>

      {!loading && rows.length === 0 && (
        <p className="empty-note">
          Nothing suppressed. Every address we have ever sent to has either arrived or failed
          temporarily.
        </p>
      )}

      {shown.length > 0 && (
        <ul className="supp__list">
          {shown.map((r) => {
            const st = style(r.reason)
            return (
              <li className="supp__row" key={r.email}>
                <span className="supp__email">{r.email}</span>
                <span className="pill" style={{ background: st.bg, color: st.color }}>
                  {st.label}
                </span>
                <span className="supp__detail" title={r.detail}>
                  {r.detail || '—'}
                </span>
                <span className="supp__when">{r.at ? formatDate(r.at) : '—'}</span>
              </li>
            )
          })}
        </ul>
      )}

      {!loading && rows.length > 0 && shown.length === 0 && (
        <p className="empty-note">No suppressed address matches that. Clear the search to see the whole list.</p>
      )}

      {rows.length > 0 && (
        <p className="supp__note">
          Written by delivery tracking, never by hand. An address leaves this list only if the
          person asks to be mailed again.
        </p>
      )}
    </section>
  )
}
